/**
 * What the AI Studio shows, kept for the page session (Robert, 14.10.2026): coming back to the studio or
 * switching conversations shows the last data at once instead of loading it again, and refreshes it in the
 * background (stale-while-revalidate) when it is older than the ttl, when the browser tab comes back into
 * view, or when invalidate() marked it outdated (a change to the agent moves its success score); refresh()
 * reloads on demand (the welcome's refresh icon). Keys carry the agent ("overview:<botId>"). A reload of
 * the page starts empty.
 */
import { useEffect, useRef, useState } from 'react';

// a mount within this time of the last load does not load again (switching conversations)
export const TTL_MS = 10000;

const entries = new Map(); // key -> { data, at, fetcher, promise }
const listeners = new Map(); // key -> Set<(data) => void>

function notify(key) {
  const data = entries.get(key)?.data;
  (listeners.get(key) || []).forEach((fn) => fn(data));
}

/** Load (or reload) a key; requests for the same key share one call. Keeps the last data on failure. */
export function load(key, fetcher) {
  const e = entries.get(key) || {};
  entries.set(key, e);
  if (fetcher) e.fetcher = fetcher;
  if (e.promise) return e.promise;
  if (!e.fetcher) return Promise.resolve(e.data);
  e.promise = Promise.resolve()
    .then(() => e.fetcher())
    .then((data) => { e.data = data; e.at = Date.now(); return data; })
    .finally(() => { e.promise = null; notify(key); });
  return e.promise;
}

export const peek = (key) => entries.get(key)?.data;

/** Store data that came another way (e.g. a spec the builder pushed). */
export function put(key, data) {
  const e = entries.get(key) || {};
  e.data = data;
  e.at = Date.now();
  entries.set(key, e);
  notify(key);
}

/** Warm a key if it is missing or older than ttl (nothing waits for it). */
export function prefetch(key, fetcher, ttl = TTL_MS) {
  const e = entries.get(key);
  if (!e || !e.at || Date.now() - e.at > ttl) load(key, fetcher).catch(() => {});
}

/** Mark keys outdated: the next mount loads them again (whatever their age). */
export function invalidate(...keys) {
  keys.forEach((k) => { const e = entries.get(k); if (e) e.at = 0; });
}

/** Reload what is on screen and outdated (the browser tab came back: something may have changed elsewhere). */
function revalidateShown() {
  listeners.forEach((set, key) => {
    const e = entries.get(key);
    if (set.size && e?.fetcher && (!e.at || Date.now() - e.at > TTL_MS)) load(key).catch(() => {});
  });
}
if (typeof window !== 'undefined') {
  window.addEventListener('focus', revalidateShown);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') revalidateShown(); });
}

/** Reload every loaded key that starts with one of the prefixes. */
export function refresh(...prefixes) {
  const keys = [...entries.keys()].filter((k) => prefixes.some((p) => k.startsWith(p)) && entries.get(k).fetcher);
  return Promise.all(keys.map((k) => load(k).catch(() => null)));
}

/** The data of `key` (null while there is none): served from the cache, loaded when missing or stale. */
export function useCached(key, fetcher, { ttl = TTL_MS } = {}) {
  const [data, setData] = useState(() => (key ? peek(key) ?? null : null));
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  useEffect(() => {
    if (!key) {
      setData(null);
      return undefined;
    }
    const fn = (d) => setData(d ?? null);
    const set = listeners.get(key) || new Set();
    set.add(fn);
    listeners.set(key, set);
    setData(peek(key) ?? null);
    const e = entries.get(key);
    if (!e || !e.at || Date.now() - e.at > ttl) load(key, () => fetchRef.current()).catch(() => {});
    else if (!e.fetcher) e.fetcher = () => fetchRef.current();
    return () => { set.delete(fn); };
  }, [key, ttl]);
  return data;
}

/** For tests: forget everything. */
export function clearStudioCache() {
  entries.clear();
}
